import React from "react";

import * as RTooltip from "@radix-ui/react-tooltip";
import { cx } from "class-variance-authority";

import { rawSpacing } from "../../styles/theme.css";

import * as theme from "./Tooltip.css";

// Arrow is 16 wide × 8 tall pointing down; Radix rotates it for the other sides.
const ARROW_WIDTH = rawSpacing[4];
const ARROW_HEIGHT = rawSpacing[2];

// Keeps the arrow clear of the rounded corners on start/end aligned placements.
const ARROW_PADDING = rawSpacing[4];

const COLLISION_PADDING = rawSpacing[2];

type TooltipPlacement =
    | "top"
    | "left"
    | "right"
    | "bottom"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight"
    | "leftTop"
    | "leftBottom"
    | "rightTop"
    | "rightBottom";

interface TooltipProps {
    title: React.ReactNode;
    children: React.ReactNode;
    placement?: TooltipPlacement;
    mouseEnterDelay?: number;
    overlayClassName?: string;
    className?: string;
    open?: boolean;
    noWrapper?: boolean;
    disabled?: boolean;
    /** Hide the pointer without moving the bubble. */
    arrow?: boolean;
}

export function Tooltip({
    title: text,
    children,
    overlayClassName,
    open,
    className,
    noWrapper = false,
    placement = "top",
    mouseEnterDelay = 100,
    disabled = false,
    arrow = true,
}: TooltipProps) {
    if (disabled) {
        return <>{children}</>;
    }
    const { side, align } = getTooltipPosition(placement);
    const content = noWrapper ? (
        children
    ) : (
        <div className={cx(className, theme.wrapperElement)}>{children}</div>
    );
    return (
        <RTooltip.Provider>
            <RTooltip.Root delayDuration={mouseEnterDelay} open={open}>
                <RTooltip.Trigger asChild>{content}</RTooltip.Trigger>
                <RTooltip.Portal>
                    <RTooltip.Content
                        // Radix already offsets by the measured arrow height, so
                        // add it back only when there is no arrow to measure.
                        sideOffset={arrow ? 0 : ARROW_HEIGHT}
                        side={side}
                        align={align}
                        arrowPadding={ARROW_PADDING}
                        collisionPadding={COLLISION_PADDING}
                        className={cx(overlayClassName, theme.content)}
                    >
                        {text}
                        {arrow && (
                            <RTooltip.Arrow
                                className={theme.arrow}
                                width={ARROW_WIDTH}
                                height={ARROW_HEIGHT}
                            />
                        )}
                    </RTooltip.Content>
                </RTooltip.Portal>
            </RTooltip.Root>
        </RTooltip.Provider>
    );
}

function getTooltipPosition(placement: TooltipPlacement) {
    switch (placement) {
        case "top":
            return { side: "top" as const, align: "center" as const };
        case "bottom":
            return { side: "bottom" as const, align: "center" as const };
        case "left":
            return { side: "left" as const, align: "center" as const };
        case "right":
            return { side: "right" as const, align: "center" as const };
        case "topLeft":
            return { side: "top" as const, align: "start" as const };
        case "topRight":
            return { side: "top" as const, align: "end" as const };
        case "bottomLeft":
            return { side: "bottom" as const, align: "start" as const };
        case "bottomRight":
            return { side: "bottom" as const, align: "end" as const };
        case "leftTop":
            return { side: "left" as const, align: "start" as const };
        case "leftBottom":
            return { side: "left" as const, align: "end" as const };
        case "rightTop":
            return { side: "right" as const, align: "start" as const };
        case "rightBottom":
            return { side: "right" as const, align: "end" as const };
        default:
            return { side: "top" as const, align: "center" as const };
    }
}

export type { TooltipPlacement, TooltipProps };
